import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng';
import { BaseDataService } from './base.data.service';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';

export interface FlatMenuItem {
  label: string;
  path: string;
  routerLink: string;
}

/**
 * Sidebar pages the user has pinned, keyed by router link. Only enabled for VME users.
 */
@Injectable({
  providedIn: 'root',
})
export class MenuPinService extends BaseDataService {
  public url = '/api/menu-pins';

  public enabled = false;
  public pinnedLinks: string[] = [];

  // Pinned pages that are in the user's menu, in pin order
  public pinnedItems: FlatMenuItem[] = [];

  // The user's menu after security; pins outside it are dropped on the next save
  private availableItems: FlatMenuItem[] = [];
  private menuHidden = false;

  private saving: Promise<unknown> = Promise.resolve();
  private pendingSaves = 0;
  private saveFailed = false;

  constructor(
    public http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    private messageService: MessageService,
  ) {
    super(http, handlersFactory);
  }

  public async load() {
    this.setPinned(await this.getPins());
    this.enabled = true;
  }

  /**
   * @param menuHidden the menu is temporarily cut down (terms & conditions), so it mustn't prune pins
   */
  public setAvailableItems(items: FlatMenuItem[], menuHidden = false) {
    this.availableItems = items;
    this.menuHidden = menuHidden;
    this.refreshPinnedItems();
  }

  public isPinned(routerLink: string): boolean {
    return this.pinnedLinks.includes(routerLink);
  }

  public toggle(event: Event, routerLink: string) {
    event.preventDefault();
    event.stopPropagation();

    const kept = this.menuHidden
      ? this.pinnedLinks
      : this.pinnedLinks.filter((x) => this.availableItems.some((item) => item.routerLink === x));

    const links = this.isPinned(routerLink)
      ? kept.filter((x) => x !== routerLink)
      : [...kept, routerLink];

    this.setPinned(links);
    this.pendingSaves++;

    // Chained so two quick clicks can't land on the server out of order
    this.saving = this.saving
      .then(() => this.http.put<string[]>(this.url, links).toPromise())
      .catch((error) => {
        this.saveFailed = true;
        this.messageService.add({
          severity: 'error',
          summary: 'Pinned pages not saved',
          detail: error?.error?.validationErrors?.[0]?.errorMessage ?? 'Please try again.',
        });
      })
      .then(() => this.afterSave());
  }

  // Later saves in the queue may still have stored a failed pin, so once the queue is empty
  // the sidebar reloads what the server actually has rather than rolling back locally
  private async afterSave() {
    this.pendingSaves--;
    if (this.pendingSaves > 0 || !this.saveFailed) return;

    try {
      const links = await this.getPins();
      if (this.pendingSaves > 0) return; // a newer save reloads once it finishes

      this.saveFailed = false;
      this.setPinned(links);
    } catch {
      // Still flagged, so the next save reloads instead
    }
  }

  private async getPins(): Promise<string[]> {
    return (await this.http.get<string[]>(this.url).toPromise()) ?? [];
  }

  private setPinned(links: string[]) {
    this.pinnedLinks = links;
    this.refreshPinnedItems();
  }

  private refreshPinnedItems() {
    this.pinnedItems = this.pinnedLinks
      .map((link) => this.availableItems.find((item) => item.routerLink === link))
      .filter((item) => !!item);
  }
}
